import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Checkbox,
  Toolbar,
  Breadcrumbs,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  FileDownload as FileDownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  CreateNewFolder as CreateNewFolderIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/authStore';
import {
  listFiles,
  deleteFile as deleteFileApi,
  downloadFile,
  previewFile as previewFileApi,
  listStorages,
  listFolders,
  createFolder as createFolderApi,
  uploadMultipleFiles,
  downloadZip,
} from '../api/storage';
import { formatBytes, formatDate } from '../utils/formatters';
import CsvViewer from '../components/common/CsvViewer';
import JsonViewer from '../components/common/JsonViewer';

const baseType = (contentType) => (contentType || '').split(';')[0].trim().toLowerCase();

// How a previewed file is rendered: 'csv' | 'json' | 'text'.
const previewKind = (file, preview) => {
  const name = (file?.name || '').toLowerCase();
  const type = baseType(preview?.content_type || file?.content_type);
  if (/\.(csv|tsv)$/.test(name) || ['text/csv', 'text/tab-separated-values', 'application/csv'].includes(type)) return 'csv';
  if (name.endsWith('.json') || type === 'application/json' || type.endsWith('+json')) return 'json';
  return 'text';
};

const rawPreSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  m: 0,
  p: 2,
  bgcolor: 'action.hover',
  borderRadius: 1,
  maxHeight: '60vh',
  overflow: 'auto',
};

const FilePreviewBody = ({ file, preview }) => {
  const kind = previewKind(file, preview);
  const content = preview.content || '';
  const json = React.useMemo(() => {
    if (kind !== 'json') return undefined;
    try { return { value: JSON.parse(content) }; } catch { return undefined; }
  }, [kind, content]);

  if (kind === 'csv') {
    const tsv = /\.tsv$/i.test(file?.name || '') || baseType(preview.content_type) === 'text/tab-separated-values';
    return (
      <CsvViewer text={content} truncated={preview.truncated} totalSize={preview.total_size}
        delimiter={tsv ? '\t' : undefined} />
    );
  }
  return (
    <>
      {preview.truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing first {formatBytes(content.length)} of {formatBytes(preview.total_size)}. Download the file to see full content.
        </Alert>
      )}
      {json ? (
        <JsonViewer value={json.value} maxHeight="60vh" />
      ) : (
        <Box component="pre" sx={rawPreSx}>{content}</Box>
      )}
    </>
  );
};

const StoragePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, checkAuth } = useAuth();
  const fileInputRef = useRef(null);

  // Storage selector state
  const [storages, setStorages] = useState([]);
  const [storageId, setStorageId] = useState(null);
  const [storagesLoading, setStoragesLoading] = useState(true);

  // Folder navigation state
  const [currentFolder, setCurrentFolder] = useState('');
  const [folders, setFolders] = useState([]);

  // File list state
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFiles, setTotalFiles] = useState(0);

  // Multi-select state
  const [selectedFiles, setSelectedFiles] = useState(new Set());

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFileData, setPreviewFileData] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Create folder dialog state
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Fetch storages
  const fetchStorages = useCallback(async () => {
    try {
      setStoragesLoading(true);
      const data = await listStorages();
      const storagesList = data.storages || [];
      setStorages(storagesList);
      if (storagesList.length > 0 && !storageId) {
        // Pick the default storage if one exists, otherwise the first
        const defaultStorage = storagesList.find(s => s.is_default) || storagesList[0];
        setStorageId(defaultStorage.id);
      }
    } catch (err) {
      console.error('Error fetching storages:', err);
      setError('Failed to load storages.');
    } finally {
      setStoragesLoading(false);
    }
  }, [storageId]);

  // Fetch folders for current path
  const fetchFolders = useCallback(async () => {
    if (!storageId) return;
    try {
      const data = await listFolders(storageId, { parent: currentFolder });
      setFolders(data.folders || []);
    } catch (err) {
      console.error('Error fetching folders:', err);
      // Non-blocking: folders might just be empty
      setFolders([]);
    }
  }, [storageId, currentFolder]);

  // Fetch files from the API
  const fetchFiles = useCallback(async () => {
    if (!isAuthenticated || !storageId) return;

    try {
      setLoading(true);
      setError('');

      const data = await listFiles(storageId, {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: searchTerm,
        folder: currentFolder,
      });

      setFiles(data.files || []);
      setTotalFiles(data.total || 0);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(err.message || 'An error occurred while fetching files');

      if (err.response?.status === 401 ||
          err.message?.includes('expired') ||
          err.message?.includes('authentication')) {
        navigate('/login', { state: { from: '/storage' } });
      }
    } finally {
      setLoading(false);
    }
  }, [storageId, page, rowsPerPage, searchTerm, currentFolder, isAuthenticated, navigate]);

  // Initial data load and authentication check
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      if (!isMounted) return;

      try {
        if (!isAuthenticated) {
          const authValid = await checkAuth();
          if (!authValid) {
            navigate('/login', { state: { from: '/storage' } });
            return;
          }
        }

        if (isAuthenticated) {
          await fetchStorages();
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error in initial data load:', err);
        setError(err.message || 'An error occurred while loading data');

        if (err.response?.status === 401 ||
            err.message?.includes('expired') ||
            err.message?.includes('authentication')) {
          navigate('/login', { state: { from: '/storage' } });
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, checkAuth, navigate, fetchStorages]);

  // Fetch files and folders when storage/folder/pagination/search changes
  useEffect(() => {
    if (isAuthenticated && storageId) {
      fetchFiles();
      fetchFolders();
    }
  }, [storageId, currentFolder, page, rowsPerPage, searchTerm, isAuthenticated, fetchFiles, fetchFolders]);

  // Reset page & selection when folder changes
  useEffect(() => {
    setPage(0);
    setSelectedFiles(new Set());
  }, [currentFolder, storageId]);

  // -- Folder navigation --
  const navigateToFolder = (folderPath) => {
    setCurrentFolder(folderPath);
  };

  const breadcrumbSegments = () => {
    if (!currentFolder) return [];
    const parts = currentFolder.split('/').filter(Boolean);
    // Skip the storage_id prefix (first segment is always the numeric storage id)
    return parts.length > 0 && parts[0] === String(storageId) ? parts.slice(1) : parts;
  };

  const breadcrumbPathAt = (index) => {
    const segments = breadcrumbSegments();
    // Reconstruct with storage_id prefix
    const pathSegments = segments.slice(0, index + 1);
    return `${storageId}/${pathSegments.join('/')}`;
  };

  // -- Selection --
  const handleToggleSelect = (fileId) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedFiles(new Set(files.map(f => f.id)));
    } else {
      setSelectedFiles(new Set());
    }
  };

  const allSelected = files.length > 0 && selectedFiles.size === files.length;
  const someSelected = selectedFiles.size > 0 && selectedFiles.size < files.length;

  // -- Download zip --
  const handleDownloadZip = async () => {
    if (selectedFiles.size === 0 || !storageId) return;
    try {
      const blob = await downloadZip(storageId, Array.from(selectedFiles));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'files.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Error downloading zip:', err);
      setError(err.message || 'Failed to download files as zip.');
    }
  };

  // -- Bulk delete --
  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0 || !storageId) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedFiles);
      for (const fileId of ids) {
        await deleteFileApi(storageId, fileId);
      }
      setSelectedFiles(new Set());
      setBulkDeleteDialogOpen(false);
      await fetchFiles();
      await fetchFolders();
    } catch (err) {
      console.error('Error bulk deleting files:', err);
      setError(err.message || 'Failed to delete some files.');
    } finally {
      setBulkDeleting(false);
    }
  };

  // -- Menu handlers --
  const handleMenuOpen = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedFile || !storageId) return;
    setDeleting(true);

    try {
      await deleteFileApi(storageId, selectedFile.id);
      await fetchFiles();
      await fetchFolders();
      setDeleteDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err.message || 'Failed to delete file.');
      if (err.response?.status === 401) {
        navigate('/login', { state: { from: '/storage' } });
      }
    } finally {
      setDeleting(false);
    }
  };

  // -- Download --
  const handleDownload = async (file) => {
    if (!storageId) return;
    try {
      const blob = await downloadFile(storageId, file.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(err.message || 'Failed to download file.');
      if (err.response?.status === 401) {
        navigate('/login', { state: { from: '/storage' } });
      }
    }
  };

  // -- Copy URL --
  const handleCopyUrl = (file) => {
    if (!storageId) return;
    const fileUrl = `${window.location.origin}/api/v1/storage/${storageId}/files/${file.id}/download`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(fileUrl);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = fileUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(textarea);
    }
    handleMenuClose();
  };

  // -- Preview --
  const isPreviewable = (contentType) => {
    if (!contentType) return false;
    const type = baseType(contentType);
    if (type.startsWith('text/')) return true;
    return ['application/json', 'application/xml', 'application/javascript', 'application/x-yaml', 'application/yaml'].includes(type);
  };

  const handlePreview = async (file) => {
    if (!storageId) return;
    setPreviewFileData(file);
    setPreviewDialogOpen(true);
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewContent(null);

    try {
      const data = await previewFileApi(storageId, file.id);
      setPreviewContent(data);
    } catch (err) {
      if (err.response?.status === 415) {
        setPreviewError('Preview is not available for this file type.');
      } else if (err.response?.status === 404) {
        setPreviewError('File content not found on disk.');
      } else {
        setPreviewError(err.message || 'Failed to load preview.');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    setPreviewDialogOpen(false);
    setPreviewFileData(null);
    setPreviewContent(null);
    setPreviewError('');
  };

  // -- Upload dialog --
  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
    setUploadFiles([]);
    setUploadResult(null);
  };

  const handleUploadDialogClose = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
    setUploadFiles([]);
    setUploadResult(null);
  };

  const handleFileSelect = (event) => {
    const selected = Array.from(event.target.files || []);
    setUploadFiles(prev => [...prev, ...selected]);
  };

  const handleRemoveUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    setUploadFiles(prev => [...prev, ...dropped]);
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0 || !storageId) return;
    setUploading(true);
    setUploadResult(null);

    try {
      const result = await uploadMultipleFiles(storageId, uploadFiles, {
        folder: currentFolder,
      });
      setUploadResult(result);
      // Refresh file list and folders after upload
      await fetchFiles();
      await fetchFolders();
      // Clear selected files on success
      if (result.failed?.length === 0) {
        setTimeout(() => {
          setUploadDialogOpen(false);
          setUploadFiles([]);
          setUploadResult(null);
        }, 1500);
      }
    } catch (err) {
      console.error('Error uploading files:', err);
      setUploadResult({ error: err.message || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  // -- Create folder dialog --
  const handleCreateFolderOpen = () => {
    setCreateFolderDialogOpen(true);
    setNewFolderName('');
  };

  const handleCreateFolderClose = () => {
    if (creatingFolder) return;
    setCreateFolderDialogOpen(false);
    setNewFolderName('');
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !storageId) return;
    setCreatingFolder(true);

    try {
      const fullPath = currentFolder
        ? `${currentFolder}/${newFolderName.trim()}`
        : newFolderName.trim();
      await createFolderApi(storageId, fullPath);
      setCreateFolderDialogOpen(false);
      setNewFolderName('');
      await fetchFolders();
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err.message || 'Failed to create folder.');
    } finally {
      setCreatingFolder(false);
    }
  };

  // -- Pagination --
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // -- Storage change handler --
  const handleStorageChange = (event) => {
    setStorageId(event.target.value);
    setCurrentFolder('');
    setSearchTerm('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Storage
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={handleCreateFolderOpen}
            disabled={!storageId}
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadDialogOpen}
            disabled={!storageId}
          >
            Upload Files
          </Button>
        </Box>
      </Box>

      {/* Storage selector */}
      {storagesLoading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : storages.length > 0 ? (
        <FormControl size="small" sx={{ mb: 2, minWidth: 250 }}>
          <InputLabel id="storage-select-label">Storage</InputLabel>
          <Select
            labelId="storage-select-label"
            value={storageId || ''}
            label="Storage"
            onChange={handleStorageChange}
          >
            {storages.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}{s.is_default ? ' (default)' : ''} - {s.type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          No storages configured. Create a storage to get started.
        </Alert>
      )}

      {/* Breadcrumb navigation */}
      {storageId && (
        <Paper sx={{ px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center' }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="folder breadcrumb">
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigateToFolder('')}
              color={currentFolder ? 'inherit' : 'primary'}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Root
            </MuiLink>
            {breadcrumbSegments().map((segment, index) => {
              const isLast = index === breadcrumbSegments().length - 1;
              return (
                <MuiLink
                  key={index}
                  component="button"
                  variant="body2"
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigateToFolder(breadcrumbPathAt(index))}
                  color={isLast ? 'primary' : 'inherit'}
                >
                  {segment}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Selection toolbar or normal header */}
        {selectedFiles.size > 0 ? (
          <Toolbar
            sx={{
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              mb: 2,
              bgcolor: 'primary.light',
              borderRadius: 1,
              color: 'primary.contrastText',
            }}
          >
            <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">
              {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected
            </Typography>
            <Tooltip title="Download selected as zip">
              <Button
                variant="contained"
                size="small"
                startIcon={<ArchiveIcon />}
                onClick={handleDownloadZip}
                sx={{ mr: 1, whiteSpace: 'nowrap' }}
              >
                Download ({selectedFiles.size})
              </Button>
            </Tooltip>
            <Tooltip title="Delete selected">
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDeleteClick}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Delete ({selectedFiles.size})
              </Button>
            </Tooltip>
          </Toolbar>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {currentFolder ? currentFolder.split('/').pop() : 'Your Files'}
            </Typography>

            <TextField
              variant="outlined"
              size="small"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    disabled={files.length === 0}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Visibility</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {/* Folder rows */}
                  {folders.map((folder) => (
                    <TableRow
                      key={`folder-${folder.name}`}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        navigateToFolder(folder.path);
                      }}
                    >
                      <TableCell padding="checkbox" />
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FolderIcon sx={{ mr: 1, color: 'warning.main' }} />
                          <Typography variant="body2" fontWeight={500}>
                            {folder.name}
                          </Typography>
                          {folder.file_count > 0 && (
                            <Chip
                              label={`${folder.file_count} file${folder.file_count !== 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>--</TableCell>
                      <TableCell>Folder</TableCell>
                      <TableCell>--</TableCell>
                      <TableCell>--</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  ))}

                  {/* File rows */}
                  {files.length === 0 && folders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Box sx={{ opacity: 0.5 }}>
                          <FileIcon sx={{ fontSize: 48, mb: 1 }} />
                          <Typography>
                            {currentFolder
                              ? 'This folder is empty.'
                              : 'No files found. Upload a file to get started.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    files.map((file) => (
                      <TableRow key={file.id} hover selected={selectedFiles.has(file.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedFiles.has(file.id)}
                            onChange={() => handleToggleSelect(file.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {file.name}
                              </Typography>
                              {file.path && currentFolder === '' && (
                                <Typography variant="caption" color="text.secondary">
                                  {file.path}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{formatBytes(file.size)}</TableCell>
                        <TableCell>{file.content_type || 'Unknown'}</TableCell>
                        <TableCell>{formatDate(file.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={file.is_public ? 'Public' : 'Private'}
                            size="small"
                            color={file.is_public ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isPreviewable(file.content_type) && (
                            <Tooltip title="Preview">
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(file)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(file)}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, file)}
                            title="More options"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && files.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalFiles}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* File actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedFile && handleDownload(selectedFile)}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedFile && handleCopyUrl(selectedFile)}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error.main' }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog (single file) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{selectedFile?.name}&quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !bulkDeleting && setBulkDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete {selectedFiles.size} File{selectedFiles.size !== 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedFiles.size} selected file{selectedFiles.size !== 1 ? 's' : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteDialogOpen(false)}
            disabled={bulkDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={20} /> : null}
          >
            {bulkDeleting ? 'Deleting...' : `Delete ${selectedFiles.size} File${selectedFiles.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File preview dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handlePreviewClose}
        maxWidth={previewKind(previewFileData) === 'csv' ? 'lg' : 'md'}
        fullWidth
      >
        <DialogTitle>
          {previewFileData?.name}
          <Typography variant="caption" display="block" color="text.secondary">
            {previewFileData?.content_type} &mdash; {formatBytes(previewFileData?.size)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {previewError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}
          {previewContent && <FilePreviewBody file={previewFileData} preview={previewContent} />}
        </DialogContent>
        <DialogActions>
          {previewFileData && (
            <Button
              onClick={() => handleDownload(previewFileData)}
              startIcon={<FileDownloadIcon />}
            >
              Download
            </Button>
          )}
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Files
          {currentFolder && (
            <Typography variant="caption" display="block" color="text.secondary">
              Uploading to: /{currentFolder}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Drag & drop zone */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragOver ? 'primary.50' : 'transparent',
              transition: 'all 0.2s',
              mb: 2,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              Drag & drop files here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Up to 50 files per upload
            </Typography>
          </Box>

          {/* Selected files list */}
          {uploadFiles.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected
              </Typography>
              {uploadFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <FileIcon sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} fontSize="small" />
                    <Typography variant="body2" noWrap sx={{ mr: 1 }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      ({formatBytes(file.size)})
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveUploadFile(index)}
                    disabled={uploading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Upload progress / result */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Uploading {uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''}...
              </Typography>
            </Box>
          )}

          {uploadResult && !uploadResult.error && (
            <Alert severity={uploadResult.failed?.length > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {uploadResult.successful} of {uploadResult.total} file{uploadResult.total !== 1 ? 's' : ''} uploaded successfully.
              {uploadResult.failed?.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {uploadResult.failed.map((f, i) => (
                    <li key={i}>{f.filename}: {f.error}</li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {uploadResult?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadResult.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            {uploadResult ? 'Close' : 'Cancel'}
          </Button>
          {!uploadResult && (
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={uploading || uploadFiles.length === 0}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} File${uploadFiles.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create folder dialog */}
      <Dialog
        open={createFolderDialogOpen}
        onClose={handleCreateFolderClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          {currentFolder && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Creating in: /{currentFolder}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) {
                handleCreateFolder();
              }
            }}
            disabled={creatingFolder}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateFolderClose} disabled={creatingFolder}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={creatingFolder || !newFolderName.trim()}
            startIcon={creatingFolder ? <CircularProgress size={20} /> : <CreateNewFolderIcon />}
          >
            {creatingFolder ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StoragePage;
